import { LoaderOverlay, Popover, Stack, Theme, Typography } from '@common-components';
import { useCurrentUser } from 'hooks';
import { popupMinHeight } from 'broker/pages/SubmissionWorkspacePage/components/AIGeneratedContent/constants';
import { AIBubbleHeader } from './AIBubbleHeader';
import { AIBubbleItem } from './AIBubbleItem';
import { DemoStep } from './steps/shared-steps';

interface RenewalDemoAIBubbleProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  renderedSteps: Set<number>;
  onStepRendered: (itemIndex: number) => void;
  isPaused: boolean;
  togglePause: () => void;
  demoSteps: DemoStep[];
  isLoading: boolean;
}

const PopupWidth = 720;

export function RenewalDemoAIBubble({
  anchorEl,
  open,
  onClose,
  renderedSteps,
  onStepRendered,
  isPaused,
  togglePause,
  demoSteps,
  isLoading,
}: RenewalDemoAIBubbleProps) {
  const { me } = useCurrentUser();

  return (
    <Popover
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      PaperProps={{
        sx: {
          width: PopupWidth,
          minHeight: popupMinHeight,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
      sx={{
        zIndex: (theme: Theme) => theme.zIndex.tooltip,
        left: -10,
      }}
    >
      <AIBubbleHeader
        onClose={onClose}
        isPaused={isPaused}
        togglePause={togglePause}
        isFinished={renderedSteps.size === demoSteps.length}
      />
      {isLoading ? (
        <LoaderOverlay />
      ) : (
        <Stack m={2} gap={1}>
          <Typography variant="body2" mb={1}>
            {`Hey ${me?.firstName}, let's start working on this renewal.`}
          </Typography>

          {demoSteps.map((step, index) => {
            // Only render if:
            // - It's the first item
            // - current item has been rendered before
            // - previous item has been rendered and we are not paused
            const shouldRender = index === 0 || renderedSteps.has(index) || (renderedSteps.has(index - 1) && !isPaused);

            if (!shouldRender) {
              return null;
            }

            return (
              <AIBubbleItem
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                description={step.description}
                action={step.action}
                isActionExecuted={renderedSteps.has(index)}
                setIsActionExecuted={() => onStepRendered(index)}
              />
            );
          })}
        </Stack>
      )}
    </Popover>
  );
}
